import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { useIsAdminTier } from '@/shared/auth/SessionProvider';
import { CrudTable, type CrudColumn } from '@/shared/components/ui/crud-table';
import { StatusBadge } from '@/shared/components/ui/status-badge';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import { Label } from '@/shared/components/ui/label';
import type { ListItem } from '@/shared/api/SharePointListClient';
import { invalidateAbmRelatedQueries, type AbmKind } from '@/shared/api/abmCacheInvalidation';
import { useActiveCountry } from '@/shared/countries/ActiveCountryProvider';
import type { EntityViewProps } from './types';

/**
 * Shared ABM for entities whose only editable field is a single "name" column
 * (plus a status column for soft-delete). Used by Medios de Contactos,
 * Categorías, and Percepción / Impuesto.
 */
export type SimpleAbmAdapter<TFields extends object> = {
  /** TanStack Query key. */
  queryKey: readonly string[];
  /**
   * ABM tag for cross-feature cache invalidation. Drives
   * `invalidateAbmRelatedQueries` after every successful mutation so
   * cached views in other features (Compras, Cotización, …) refresh
   * immediately instead of waiting on staleTime.
   */
  abmKind: AbmKind;
  /** Fetch every row (Activo + Inactivo). */
  listAll: () => Promise<ListItem<TFields>[]>;
  /** Create a new row from the entered value. */
  create: (value: string) => Promise<ListItem<TFields>>;
  /** Update the name on an existing row. */
  update: (id: string, value: string) => Promise<ListItem<TFields>>;
  /** Soft-delete via status column. */
  softDelete: (id: string) => Promise<ListItem<TFields>>;
  /** Pluck the displayed value from an item. */
  valueOf: (item: ListItem<TFields>) => string;
  /** Pluck the status literal from an item (e.g. 'Activo' / 'Inactivo'). */
  statusOf: (item: ListItem<TFields>) => string | null | undefined;
  /** Label of the editable field (e.g. "Medio", "Categoría"). */
  fieldLabel: string;
  /** Placeholder for the input (e.g. "Ingresar categoría"). */
  fieldPlaceholder: string;
  /** Entity singular, used in dialog titles ("Nuevo X", "Editar X"). */
  entitySingular: string;
  /** "Nueva X" en vez de "Nuevo X". */
  femenino?: boolean;
  /** Success messages for the status dialog. */
  createdMessage: string;
  updatedMessage: string;
  deletedMessage: string;
};

type Mode<T> =
  | { kind: 'closed' }
  | { kind: 'create' }
  | { kind: 'edit'; item: T }
  | { kind: 'delete'; item: T };

export function SimpleAbmEntity<TFields extends object>({
  adapter,
  search,
  filterValue,
  onStatus,
  openCreate,
  onCreateHandled,
}: EntityViewProps & { adapter: SimpleAbmAdapter<TFields> }) {
  const queryClient = useQueryClient();
  // Append the active country code so AR/MX caches don't share a slot
  // even though the adapter's static `queryKey` is country-blind.
  // `queryClient.clear()` on country switch handles invalidation
  // globally; this is defense-in-depth so direct getQueryData reads
  // can't return wrong-country data. Existing prefix-only
  // invalidations (`invalidateQueries({ queryKey: adapter.queryKey })`)
  // still match because country is appended at the tail.
  const { config: activeCountry } = useActiveCountry();
  const scopedKey = useMemo(
    () => [...adapter.queryKey, activeCountry.code] as readonly unknown[],
    [adapter.queryKey, activeCountry.code],
  );
  const { data: rows = [], isPending, isFetching, error, refetch } = useQuery({
    queryKey: scopedKey,
    queryFn: adapter.listAll,
    staleTime: 60_000,
  });

  const canEdit = useIsAdminTier();
  const [mode, setMode] = useState<Mode<ListItem<TFields>>>({ kind: 'closed' });

  useEffect(() => {
    if (openCreate) {
      setMode({ kind: 'create' });
      onCreateHandled();
    }
  }, [openCreate, onCreateHandled]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    let sorted = [...rows].sort((a, b) =>
      adapter.valueOf(a).localeCompare(adapter.valueOf(b), 'es'),
    );
    if (filterValue) {
      sorted = sorted.filter((r) => adapter.statusOf(r) === filterValue);
    }
    if (!q) return sorted;
    return sorted.filter((r) => adapter.valueOf(r).toLowerCase().includes(q));
  }, [rows, search, filterValue, adapter]);

  const columns: CrudColumn<ListItem<TFields>>[] = [
    {
      header: 'Status',
      width: 'w-32',
      render: (r) => <StatusBadge status={adapter.statusOf(r)} />,
    },
    {
      header: adapter.fieldLabel,
      render: (r) => (
        <span className="font-medium text-foreground">{adapter.valueOf(r) || '—'}</span>
      ),
    },
  ];

  const closeDialog = () => setMode({ kind: 'closed' });

  const handleMutated = (message: string) => {
    queryClient.invalidateQueries({ queryKey: adapter.queryKey });
    invalidateAbmRelatedQueries(queryClient, adapter.abmKind);
    closeDialog();
    onStatus({ kind: 'success', message });
  };

  const handleMutationError = (err: unknown) => {
    const message = err instanceof Error ? err.message : 'No se pudo completar la operación.';
    closeDialog();
    onStatus({ kind: 'error', message });
  };

  return (
    <>
      <CrudTable
        rows={filtered}
        columns={columns}
        loading={isPending}
        error={error instanceof Error ? error : null}
        onRetry={() => refetch()}
        emptyMessage={search ? `No hay resultados para "${search}".` : 'Sin registros.'}
        onEdit={(r) => setMode({ kind: 'edit', item: r })}
        onDelete={(r) => setMode({ kind: 'delete', item: r })}
        canEdit={(r) => adapter.statusOf(r) !== 'Inactivo'}
        editDisabledTitle={() => 'El registro está inactivo'}
        canDelete={(r) => adapter.statusOf(r) !== 'Inactivo'}
        deleteDisabledTitle={() => 'El registro ya está inactivo'}
        // Hide the entire actions column for non-admin roles —
        // Configuración writes are admin-only.
        hideActions={!canEdit}
        isBackgroundFetching={isFetching}
      />

      {(mode.kind === 'create' || mode.kind === 'edit') && (
        <SimpleFieldDialog
          adapter={adapter}
          open
          onOpenChange={(o) => !o && closeDialog()}
          item={mode.kind === 'edit' ? mode.item : null}
          existingRows={rows}
          onSuccess={handleMutated}
          onError={handleMutationError}
        />
      )}

      {mode.kind === 'delete' && (
        <SimpleDeleteDialog
          adapter={adapter}
          open
          onOpenChange={(o) => !o && closeDialog()}
          item={mode.item}
          onSuccess={handleMutated}
          onError={handleMutationError}
        />
      )}
    </>
  );
}

// ---------- internal dialogs ----------

function SimpleFieldDialog<TFields extends object>({
  adapter,
  open,
  onOpenChange,
  item,
  existingRows,
  onSuccess,
  onError,
}: {
  adapter: SimpleAbmAdapter<TFields>;
  open: boolean;
  onOpenChange: (o: boolean) => void;
  item: ListItem<TFields> | null;
  existingRows: ListItem<TFields>[];
  onSuccess: (message: string) => void;
  onError: (err: unknown) => void;
}) {
  const isEdit = Boolean(item);
  const schema = z.object({
    value: z
      .string()
      .trim()
      .min(1, `Ingresá ${adapter.fieldLabel.toLowerCase()}`),
  });
  type Values = z.infer<typeof schema>;

  const form = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: { value: item ? adapter.valueOf(item) : '' },
  });

  useEffect(() => {
    if (open) form.reset({ value: item ? adapter.valueOf(item) : '' });
  }, [open, item, form, adapter]);

  const mutation = useMutation({
    mutationFn: async ({ value }: Values) => {
      if (isEdit && item) return adapter.update(item.id, value);
      return adapter.create(value);
    },
    onSuccess: () => onSuccess(isEdit ? adapter.updatedMessage : adapter.createdMessage),
    onError,
  });

  const watchedValue = form.watch('value');
  const missingFields = !watchedValue?.trim() ? [adapter.fieldLabel] : [];
  const canSubmit = missingFields.length === 0;

  const onSubmit = form.handleSubmit((values) => {
    const normalized = values.value.trim().toLowerCase();
    const duplicated = existingRows.find(
      (r) => r.id !== item?.id && adapter.valueOf(r).trim().toLowerCase() === normalized,
    );
    if (duplicated) {
      form.setError('value', { type: 'manual', message: `Ya existe con ese ${adapter.fieldLabel.toLowerCase()}.` });
      return;
    }
    mutation.mutate(values);
  });

  return (
    <Dialog open={open} onOpenChange={(o) => !mutation.isPending && onOpenChange(o)}>
      <DialogContent
        className="sm:max-w-md"
        processing={mutation.isPending && (isEdit ? 'Guardando cambios…' : 'Creando…')}
      >
        <DialogHeader>
          <DialogTitle>
            {isEdit ? `Editar ${adapter.entitySingular}` : `${adapter.femenino ? 'Nueva' : 'Nuevo'} ${adapter.entitySingular}`}
          </DialogTitle>
          <DialogDescription>
            {isEdit
              ? `Modificá el valor y guardá los cambios.`
              : `Ingresá el nombre para dar de alta un nuevo ${adapter.entitySingular.toLowerCase()}.`}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={onSubmit} className="flex flex-1 flex-col overflow-hidden" noValidate>
          <DialogBody>
            <div className="space-y-1.5">
              <Label htmlFor="value">
                {adapter.fieldLabel}
                <span className="text-primary"> *</span>
              </Label>
              <Input
                id="value"
                variant="filled"
                placeholder={adapter.fieldPlaceholder}
                autoFocus
                {...form.register('value')}
              />
              {form.formState.errors.value && (
                <p className="text-xs font-medium text-destructive">
                  {form.formState.errors.value.message}
                </p>
              )}
            </div>
          </DialogBody>
          <DialogFooter>
            {missingFields.length > 0 && (
              <p className="mr-auto text-xs text-muted-foreground">
                Faltan completar: <span className="font-medium text-destructive">{missingFields.join(', ')}</span>
              </p>
            )}
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={mutation.isPending}
            >
              Cancelar
            </Button>
            <Button type="submit" disabled={mutation.isPending || !canSubmit}>
              {isEdit ? 'Guardar cambios' : 'Añadir'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function SimpleDeleteDialog<TFields extends object>({
  adapter,
  open,
  onOpenChange,
  item,
  onSuccess,
  onError,
}: {
  adapter: SimpleAbmAdapter<TFields>;
  open: boolean;
  onOpenChange: (o: boolean) => void;
  item: ListItem<TFields>;
  onSuccess: (message: string) => void;
  onError: (err: unknown) => void;
}) {
  const mutation = useMutation({
    mutationFn: async () => adapter.softDelete(item.id),
    onSuccess: () => onSuccess(adapter.deletedMessage),
    onError,
  });

  return (
    <Dialog open={open} onOpenChange={(o) => !mutation.isPending && onOpenChange(o)}>
      <DialogContent
        className="sm:max-w-md"
        processing={mutation.isPending && 'Eliminando…'}
      >
        <DialogHeader>
          <DialogTitle>Eliminar {adapter.entitySingular}</DialogTitle>
          <DialogDescription>
            ¿Confirmás la eliminación de{' '}
            <strong className="text-foreground">{adapter.valueOf(item) || '—'}</strong>?
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={mutation.isPending}
          >
            Cancelar
          </Button>
          <Button
            variant="destructive"
            onClick={() => mutation.mutate()}
            disabled={mutation.isPending}
          >
            Eliminar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
